<template>
  <div class="flex min-h-screen bg-gray-50 dark:bg-gray-900">

    <!-- Sidebar -->
    <AcdSidebar :isOpen="isSidebarOpen" />

    <!-- Main content -->
    <main class="flex-1 transition-all duration-300" :class="{'ml-64': isSidebarOpen, 'ml-0': !isSidebarOpen}">
      
      <!-- Topbar -->
      <header class="fixed top-0 w-full z-50 bg-white dark:bg-gray-800 shadow">
        <AcdTopbar
          :page-title="currentPageTitle"
          :is-sidebar-open="isSidebarOpen"
          :is-dark-mode="isDarkMode"
          @toggle-sidebar="toggleSidebar"
          @toggle-dark-mode="toggleDarkMode"
        />
      </header>

      <div class="p-6 mt-16">

        <!-- Search + Add Academic Year -->
        <div class="flex justify-between mb-6 p-4">
          <input
            v-model="search"
            type="text"
            placeholder="Search academic year..."
            class="border rounded-lg px-4 py-2 w-72 dark:bg-gray-700 dark:text-white"
          />
          <button
            @click="openModal"
            class="bg-[#74bd54] text-white px-5 py-2 rounded-lg hover:bg-[#63a43f]"
          >
            + Add Academic Year
          </button>
        </div>

        <!-- Academic Years Grid -->
        <div v-if="filteredYears.length" class="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          <div v-for="year in filteredYears" :key="year.id" class="bg-white dark:bg-gray-800 p-6 rounded-xl shadow hover:shadow-lg">
            
            <div class="flex items-center justify-between mb-4">
              <div>
                <h3 class="font-semibold text-gray-800 dark:text-white">{{ year.name }}</h3>
                <p class="text-sm text-gray-500">{{ year.code }}</p>
              </div>
              <div class="flex space-x-2">
                <button @click="editYear(year)" class="text-blue-600 hover:underline text-sm">Edit</button>
                <button @click="deleteYear(year.id)" class="text-red-500 hover:underline text-sm">Delete</button>
              </div>
            </div>

            <p class="text-gray-500 text-sm mb-2">{{ year.description }}</p>
            <p class="text-xs text-gray-400">From {{ year.start_date }} to {{ year.end_date }}</p>
          </div>
        </div>

        <div v-else class="text-center text-gray-400 mt-20">No academic years created yet</div>

        <!-- Modal -->
        <Transition name="modal">
          <div v-if="showModal" class="fixed inset-0 bg-black/40 flex items-center justify-center">
            <div class="bg-white dark:bg-gray-800 rounded-xl p-6 w-[420px]">
              <h3 class="text-lg font-semibold text-[#035a76] mb-6">
                {{ editing ? "Edit Academic Year" : "Create Academic Year" }}
              </h3>
              <form @submit.prevent="saveYear" class="space-y-4">

                <input v-model="form.name" type="text" placeholder="Academic Year Name (e.g., 2025/2026)" required class="w-full border rounded p-2 dark:bg-gray-700 dark:text-white" />
                <input v-model="form.code" type="text" placeholder="Code (AY2025)" class="w-full border rounded p-2 dark:bg-gray-700 dark:text-white" />
                <input v-model="form.start_date" type="date" required class="w-full border rounded p-2 dark:bg-gray-700 dark:text-white" />
                <input v-model="form.end_date" type="date" required class="w-full border rounded p-2 dark:bg-gray-700 dark:text-white" />
                <textarea v-model="form.description" placeholder="Description" class="w-full border rounded p-2 dark:bg-gray-700 dark:text-white"></textarea>

                <div class="flex justify-end space-x-3">
                  <button type="button" @click="closeModal" class="px-4 py-2 bg-gray-300 rounded">Cancel</button>
                  <button type="submit" class="px-4 py-2 bg-[#74bd54] text-white rounded hover:bg-[#63a43f]">Save</button>
                </div>
              </form>
            </div>
          </div>
        </Transition>

      </div>
    </main>
  </div>
</template>

<script setup>
import { ref, computed, onMounted } from "vue";
import api from "@/services/api";
import AcdSidebar from "@/components/acd/AcdSidebar.vue";
import AcdTopbar from "@/components/acd/AcdTopbar.vue";

const currentPageTitle = ref("Academic Years");
const isSidebarOpen = ref(true);
const isDarkMode = ref(false);

const showModal = ref(false);
const editing = ref(false);
const search = ref("");

const academicYears = ref([]);


const form = ref({ id: null, name: "", code: "", start_date: "", end_date: "", description: "" });

const filteredYears = computed(() => academicYears.value.filter(y => y.name.toLowerCase().includes(search.value.toLowerCase())));


const fetchAcademicYears = async () => {
  try {
    const response = await api.get("AcademicYears.php");
    academicYears.value = response.data;
  } catch (error) {
    console.error(error);
  }
}

const openModal = () => {
  editing.value = false;
  form.value = { id: null, name: "", code: "", start_date: "", end_date: "", description: "" };
  showModal.value = true;
};

const editYear = (year) => {
  editing.value = true;
  form.value = { ...year };
  showModal.value = true;
};

const saveYear = async () => {
    try{
        let response; 
           
        if(editing.value){
            response = await api.put("AcademicYears.php", form.value)
            const index = academicYears.value.findIndex(y => y.id === form.value.id)
            if (index !== -1) {
                academicYears.value[index] = { ...form.value };
            }
        }else{
            response = await api.post("AcademicYears.php", form.value)
                academicYears.value.push({
                id:response.data.id,
                name:response.data.name,
                code:response.data.code,
                start_date:response.data.start_date,
                end_date:response.data.end_date,
                description:response.data.description
            });
        }
        closeModal();

  } catch (error) {
    console.error("Error saving academic year:", error);
  }
};

// const saveYear = () => {
//   if (editing.value) {
//     const index = academicYears.value.findIndex(y => y.id === form.value.id);
//     academicYears.value[index] = { ...form.value };
//   } else {
//     academicYears.value.push({ id: Date.now(), ...form.value });
//   }
//   closeModal();
// };

// const deleteYear = (id) => academicYears.value = academicYears.value.filter(y => y.id !== id);

const deleteYear = async (id) => {
try {

    await api.delete("AcademicYears.php", {
      data: { id: id }
    });

    academicYears.value = academicYears.value.filter(y => y.id !== id);

  } catch (error) {
    console.error("Error deleting academic year:", error);
  }
}

const closeModal = () => showModal.value = false;

const toggleSidebar = () => isSidebarOpen.value = !isSidebarOpen.value;
const toggleDarkMode = () => {
  isDarkMode.value = !isDarkMode.value;
  if (isDarkMode.value) document.documentElement.classList.add("dark");
  else document.documentElement.classList.remove("dark");
};

onMounted(() => {
  fetchAcademicYears();
});
</script>

<style>
.modal-enter-active,
.modal-leave-active { transition: all .25s ease; }
.modal-enter-from { opacity: 0; transform: scale(.9); }
.modal-leave-to { opacity: 0; transform: scale(.9); }
</style>